import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { invalidateCaseQueries } from "@/utils/cacheUtils.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { handleJsonApiError } from "jsonapi-zod-query";

type SaveNegotiatedAmountsParams = {
    caseUUID: string;
    negotiatedLiabilityManualOverride?: number | string | null;
    negotiatedUiuimManualOverride?: number | string | null;
};

export const useSaveNegotiatedAmounts = (): UseMutationResult<
    void,
    Error,
    SaveNegotiatedAmountsParams
> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            caseUUID,
            negotiatedLiabilityManualOverride,
            negotiatedUiuimManualOverride,
        }) => {
            // Use negotiation/save endpoint which handles partial updates
            const response = await fetch(apiUrl(`/negotiation/save/${caseUUID}`), {
                method: "POST",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify({
                    data: {
                        id: caseUUID,
                        type: "case",
                        attributes: {
                            negotiatedLiabilityManualOverride,
                            negotiatedUiuimManualOverride,
                        },
                    },
                }),
            });
            await handleJsonApiError(response);
        },
        onSuccess: async (_data, { caseUUID }) => {
            // This helper now invalidates both UUID and numeric ID case queries
            await invalidateCaseQueries(queryClient, caseUUID);
        },
    });
};
